/**
 * Non-component helpers for DocumentsPanel.tsx — kept here so the
 * `react-refresh/only-export-components` lint rule stays happy on the
 * .tsx side.
 */

import type { OrderDocumentItem, OrderDocumentCategory } from '../../types/portal';

export type DocTabId =
  | 'email'
  | 'attachment'
  | 'bol'
  | 'sia'
  | 'shipping'
  | 'post_shipment'
  | 'other'
  | 'invoice';

export interface DocTabDescriptor {
  id: DocTabId;
  label: string;
  /**
   * When set, this tab accepts uploads and they're stamped with this
   * category. ``null`` for system-only tabs (email / attachment / invoice).
   */
  uploadCategory: OrderDocumentCategory | null;
  dropzonePrompt: string;
  emptyTitle: string;
  emptyHint: string;
}

export const DOC_TABS: DocTabDescriptor[] = [
  {
    id: 'email',
    label: 'Source Email',
    uploadCategory: null,
    dropzonePrompt: '',
    emptyTitle: 'No source email',
    emptyHint: 'Source emails are linked automatically by the inbox sync.',
  },
  {
    id: 'attachment',
    label: 'Attachments',
    uploadCategory: null,
    dropzonePrompt: '',
    emptyTitle: 'No attachments',
    emptyHint: 'Attachments arrive from the email pipeline.',
  },
  {
    id: 'bol',
    label: 'BOLs',
    uploadCategory: 'special_bol',
    dropzonePrompt: 'Drop a special-BOL scan here',
    emptyTitle: 'No BOLs yet',
    emptyHint: 'Drive-synced BOLs land here automatically once the iPad signs them. You can also drop a special BOL scan above.',
  },
  {
    id: 'sia',
    label: 'SIA Paperwork',
    uploadCategory: 'sia',
    dropzonePrompt: 'Drop SIA paperwork here',
    emptyTitle: 'No SIA paperwork',
    emptyHint: 'Drop SIA forms above.',
  },
  {
    id: 'shipping',
    label: 'Shipping',
    uploadCategory: 'shipping',
    dropzonePrompt: 'Drop shipping documents here',
    emptyTitle: 'No shipping documents',
    emptyHint: 'Drop shipping paperwork above (Canadian customs forms, freight waybills, etc.).',
  },
  {
    id: 'post_shipment',
    label: 'Post-Shipment',
    uploadCategory: 'post_shipment',
    dropzonePrompt: 'Drop post-shipment documents here',
    emptyTitle: 'Nothing post-shipment yet',
    emptyHint: 'Delivery receipts, returns, and other after-the-truck paperwork.',
  },
  {
    id: 'other',
    label: 'Other',
    uploadCategory: 'general',
    dropzonePrompt: 'Drop other paperwork here',
    emptyTitle: 'Nothing else uploaded',
    emptyHint: 'Catch-all for paperwork that doesn’t fit the other tabs.',
  },
  {
    id: 'invoice',
    label: 'Invoices',
    uploadCategory: null,
    dropzonePrompt: '',
    emptyTitle: 'No invoices',
    emptyHint: 'Sage invoices appear here once the order ships.',
  },
];

/**
 * Map a single document row to the tab it belongs in. The mapping reads:
 * - System-sourced docs (email / attachment / BOL / invoice) → their
 *   own type tab.
 * - User uploads → the tab corresponding to their `category`, with a
 *   `general`/null fallback to "Other".
 *
 * Special-BOL uploads collapse into the BOLs tab so they sit alongside
 * the Drive-synced BOLs.
 */
export function classifyDoc(doc: OrderDocumentItem): DocTabId {
  switch (doc.doc_type) {
    case 'source_email':
      return 'email';
    case 'email_attachment':
      return 'attachment';
    case 'bol':
      return 'bol';
    case 'invoice':
      return 'invoice';
    case 'uploaded':
      switch (doc.category) {
        case 'special_bol':
          return 'bol';
        case 'sia':
          return 'sia';
        case 'shipping':
          return 'shipping';
        case 'post_shipment':
          return 'post_shipment';
        case 'general':
        case null:
        case undefined:
        default:
          return 'other';
      }
    default:
      return 'other';
  }
}
